/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useRef} from 'react';
import Drawer from '@material-ui/core/Drawer';
import Select from '@material-ui/core/Select';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {getValidElementId} from '@ion/core/src/utils/StringUtil';
import Hidden from '../../layout/Hidden';
import {dlsColor, dlsZIndex, isDarkPalette} from '../../theme/theme';
import {ClickAwayListener} from '../listeners/ClickAwayListener';
import {MobileSideSheetArrow} from '../sidesheet/MobileSideSheetArrow';

export interface SelectMegaMenuProps {
  children: JSX.Element | string;
  elementId?: string;
  selectFieldValue?: JSX.Element | string;
  selectFieldMinWidth?: string;
  selectFieldMaxWidth?: string;
  disableMobileDrawer?: boolean;
  menuOpen?: boolean;
  setMenuOpen?: (val: boolean) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  disabled?: boolean;
  menuWidth?: string;
  minMenuWidth?: string;
  menuLeft?: string;
  menuHeight?: string,
  menuPaddingBottom?: string
  numberOptionsSelected?: number
  menuRight?: string;
}

interface UseStylesProps {
  selectFieldMinWidth?: string;
  selectFieldMaxWidth?: string;
  menuOpen?: boolean;
  menuWidth?: string;
  minMenuWidth?: string;
  menuLeft?: string;
  menuHeight?: string;
  menuPaddingBottom?: string;
  menuRight?: string;
}

const useStyles = makeStyles(theme => ({
  megaMenu : {
    '& .MuiInputBase-root' : {
      maxWidth : ({selectFieldMaxWidth}: UseStylesProps) =>
        selectFieldMaxWidth || '300px',
      minWidth : ({selectFieldMinWidth}: UseStylesProps) =>
        selectFieldMinWidth || '236px',
      width           : '100%',
      backgroundColor : dlsColor('white'),
      padding         : '4px 0px 4px 0px !important'
    },
    '& .MuiInput-underline' : {
      borderBottom                 : `solid 1px ${dlsColor('grey5')}`,
      '&:hover:not(.Mui-disabled)' : {
        '&:before' : {
          marginBottom : '-1px',
          borderBottom : ({menuOpen}: UseStylesProps) =>
            menuOpen ? `solid 1px ${dlsColor('caBlue')}` : 'none'
        }
      },
      '&:before' : {
        marginBottom : '-1px',
        borderBottom : ({menuOpen}: UseStylesProps) =>
          menuOpen ? `solid 1px ${dlsColor('caBlue')}` : 'none'
      },
      '&:after' : {
        borderBottom : 'none'
      }
    },
    '& .MuiSelect-select.MuiSelect-select' : {
      minWidth    : '185px',
      display     : 'flex',
      paddingLeft : '15px',
      alignItems  : 'center',
      '& span'    : {
        display    : 'flex',
        alignItems : 'center'
      }
    }
  },
  selectRoot : {
    color         : dlsColor('grey3'),
    textTransform : 'uppercase',
    fontWeight    : 700,
    letterSpacing : '1px',
    fontSize      : '14px',
    lineHeight    : '18px',
    width         : '100%',
    minHeight     : '38px',
    padding       : '0px',
    maxWidth      : '200px',
    '&:focus'     : {
      backgroundColor : 'transparent'
    },
    '& [data-title=title] span' : {
      display : 'inline !important'
    }
  },
  selectIcon : {
    transform : ({menuOpen}: UseStylesProps) =>
      `rotate(${menuOpen ? '180deg' : '0deg'})`,
    transition : 'transform 300ms ease-in-out'
  },
  numberOptionsSelected : {
    color   : dlsColor('grey4'),
    padding : '0px 10px 0px 5px'
  },
  menu : {
    width     : ({menuWidth}: UseStylesProps) => menuWidth || 'calc(100% * (5/6))',
    minWidth  : ({minMenuWidth}: UseStylesProps) => minMenuWidth || 'auto',
    right     : ({menuRight}: UseStylesProps) => menuRight || 'auto',
    overflow  : 'hidden',
    maxHeight : ({menuOpen}: UseStylesProps) =>
      menuOpen ? '830px' : '0px',
    transition      : 'max-height 200ms ease-in-out',
    backgroundColor : dlsColor('white'),
    position        : 'absolute',
    left            : ({menuLeft}: UseStylesProps) => menuLeft,
    height          : ({menuHeight}: UseStylesProps) => menuHeight,
    zIndex          : dlsZIndex('selectMegaMenu'),
    boxShadow       : ({menuOpen}: UseStylesProps) => {
      const openShadow = isDarkPalette(theme)
        ? '0px 3px 6px 1px rgba(0, 0, 0, 0.5)'
        : '0px 3px 6px 1px #ccc';

      return menuOpen ? openShadow : 'none';
    }
  },
  menuContent : {
    padding       : '12px 24px',
    paddingBottom : ({menuPaddingBottom}: UseStylesProps) => menuPaddingBottom
  },
  mobileContent : {
    paddingTop    : 10,
    paddingBottom : 20
  },
  mobileSideSheetIcon : {
    borderTopRightRadius : 10,
    borderTopLeftRadius  : 10,
    maxHeight            : 'calc(100% - 30px)',
    padding              : '20px 0px'
  }
}));

/**
 * A menu that has a base select input that, when clicked on,
 * launches a mega menu under the select field with whatever child
 * content is passed to it.
 *
 * ```typescript
 *
 * import {SelectMegaMenu} from '@ion/dls/src/components/menus';
 *
 *   <SelectMegaMenu selectFieldValue={selectFieldValue}
 *     menuWidth='calc(100% - 120px)'
 *     onMenuClose={onMenuClose}
 *     menuOpen={menuOpen}
 *     setMenuOpen={setMenuOpen}>
 *     <div>HELLO</div>
 *   </SelectMegaMenu>
 *
 * ```
 *
 */
export const SelectMegaMenu = ({
  children,
  elementId,
  selectFieldValue,
  selectFieldMinWidth,
  selectFieldMaxWidth,
  menuOpen,
  setMenuOpen,
  onMenuOpen,
  onMenuClose,
  disabled = false,
  menuWidth,
  minMenuWidth,
  menuLeft,
  menuHeight,
  menuPaddingBottom,
  numberOptionsSelected,
  menuRight
}: SelectMegaMenuProps) => {
  const inputRef = useRef(null);
  const classes = useStyles({
    selectFieldMinWidth,
    selectFieldMaxWidth,
    menuOpen,
    menuWidth,
    minMenuWidth,
    menuLeft,
    menuHeight,
    menuPaddingBottom,
    menuRight
  });

  function handleMenuOpen() {
    onMenuOpen?.();
  }

  function handleMenuClose() {
    setMenuOpen(false);
    onMenuClose?.();
  }

  return (
    <div className={classnames(classes.megaMenu, menuOpen ? 'megaMenuOpen' : 'megaMenuClose')}
      id={getValidElementId(elementId)}
      data-id='megamenu'>
      <Select open={false}
        value={selectFieldValue || ''}
        renderValue={function() {
          return (
            <div data-title='title'>
              <span>{selectFieldValue}</span>
              {numberOptionsSelected &&
                <span className={classes.numberOptionsSelected}>
                  {`(${numberOptionsSelected})`}
                </span>
              }
            </div>
          );
        }}
        disabled={disabled}
        ref={inputRef}
        classes={{
          root : classes.selectRoot,
          icon : classes.selectIcon
        }}
        onClick={function() {
          setMenuOpen(!menuOpen && !disabled);
        }} />
      <Hidden show='desktop'>
        <ClickAwayListener mountListener={menuOpen}
          onClickAway={handleMenuClose}
          animationDuration={350}>
          <div className={classes.menu} id='menu'>
            <div className={classes.menuContent}>
              {children}
            </div>
          </div>
        </ClickAwayListener>
      </Hidden>
      <Hidden show='mobile'>
        <Drawer open={menuOpen}
          classes={{
            paperAnchorBottom : classes.mobileSideSheetIcon
          }}
          anchor='bottom'
          onClose={handleMenuClose}
          onOpen={handleMenuOpen}>
          <>
            <MobileSideSheetArrow smallMode />
            <div className={classes.mobileContent}>
              {children}
            </div>
          </>
        </Drawer>
      </Hidden>
    </div>
  );
};

export default SelectMegaMenu;
